import { Box, makeStyles, Paper, Table, TableBody, TableCell, TableRow, Typography } from "@material-ui/core";
import Brightness1Icon from "@material-ui/icons/Brightness1";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import ExternalLinkButton from "src/components/Button/ExternalLinkButton";
import TitleTypography from "src/components/Typography/TitleTypography";
import { COLOR_ARRAY } from "src/config/styleConfig";
import { EXPLORER_TYPE } from "src/hooks/useExplorerUrl";
import { formatAddress, numberWithCommas, toFixed } from "src/services/utils";

const useStyle = makeStyles((theme) => ({
  value: {
    color: theme.palette.text.hint,
  },
}));

const EventLinkTooltip = ({ text, hash, config }) => {
  const cls = useStyle();
  return (
    <Box display="flex" justifyContent="flex-start" alignItems="center">
      <Typography className={cls.value}>{text}</Typography>
      <ExternalLinkButton hash={hash} config={config} />
    </Box>
  );
};

const EventNormalText = ({ text, ...props }) => {
  const cls = useStyle();
  return (
    <Typography className={cls.value} {...props}>
      {text}
    </Typography>
  );
};

const EventType = ({ eventText, ...props }) => {
  const cls = useStyle();

  const color = useMemo(() => {
    if (eventText === "DEPOSIT") return COLOR_ARRAY[0];
    else if (eventText === "BORROW") return COLOR_ARRAY[1];
    else if (eventText === "WITHDRAW") return COLOR_ARRAY[2];
    else if (eventText === "REPAY") return COLOR_ARRAY[3];
  }, [eventText]);

  return (
    <Box display="flex" justifyContent="flex-start" alignItems="center">
      <Brightness1Icon style={{ color: color, fontSize: "16px", marginRight: "0.25rem" }} />
      <Typography className={cls.value} {...props}>
        {eventText}
      </Typography>
    </Box>
  );
};

const ElementConfig = [
  {
    element: "event_type",
    title: "Type",
    formatter: (data) => <EventType eventText={data.event_type} />,
  },
  {
    element: "amount",
    title: "Amount",
    formatter: (data) => <EventNormalText text={`$${numberWithCommas(toFixed(data.amount, 2))}`} />,
  },
  {
    element: "user",
    title: "User",
    formatter: (data, pool) => (
      <EventLinkTooltip text={formatAddress(data.user, 6)} hash={data.user} config={{ pool }} />
    ),
  },
  {
    element: "token",
    title: "Token",
    formatter: (data, pool) => <EventLinkTooltip text={data.symbol} hash={data.token} config={{ pool }} />,
  },
  {
    element: "tx_hash",
    title: "Transaction hash",
    formatter: (data, pool) => (
      <EventLinkTooltip
        text={formatAddress(data.tx_hash, 6)}
        hash={data.tx_hash}
        config={{ pool, type: EXPLORER_TYPE.TRANSACTION }}
      />
    ),
  },
  {
    element: "liquidator",
    title: "Liquidator",
    formatter: (data, pool) => (
      <EventLinkTooltip text={formatAddress(data.liquidator, 6)} hash={data.liquidator} config={{ pool }} />
    ),
  },
  {
    element: "debt_asset",
    title: "Debt Asset",
    formatter: (data, pool) => <EventLinkTooltip text={data.symbol_debt} hash={data.debt_asset} config={{ pool }} />,
  },
  {
    element: "collateral_asset",
    title: "Collateral Asset",
    formatter: (data, pool) => (
      <EventLinkTooltip text={data.symbol_collateral} hash={data.collateral_asset} config={{ pool }} />
    ),
  },
  {
    element: "liquidated_collateral_amount",
    title: "Liquidated Collateral Amount",
    formatter: (data) => <EventNormalText text={numberWithCommas(toFixed(data.liquidated_collateral_amount, 2))} />,
  },
  {
    element: "debt_to_cover",
    title: "Debt To Cover",
    formatter: (data) => <EventNormalText text={numberWithCommas(toFixed(data.debt_to_cover, 2))} />,
  },
];

export default function EventTvlTable({ data }) {
  const { pool } = useParams();

  return (
    <Paper>
      {data ? (
        <Table>
          <TableBody>
            {ElementConfig.map((config, index) => {
              const element = config.element;
              if (data?.[element])
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography>{config.title}</Typography>
                    </TableCell>
                    <TableCell>{config.formatter(data, pool)}</TableCell>
                  </TableRow>
                );
            })}
          </TableBody>
        </Table>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <TitleTypography textProps={{ variant: "h4" }} title="Click on chart to see data" />
        </Box>
      )}
    </Paper>
  );
}
